import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ResultStatus } from '@seahorse/common';
import { NoteModel, NoteService } from '@seahorse/domain';
import { ShButtonComponent } from '@seahorse/components';
import { ActionMenuItem } from '@seahorse/ui';
import { DialogService } from '../dialog/dialog.service';
import { ShColumn } from '../table/table.models';
import { ShTableComponent } from '../table/table/table.component';
import {
  ShCellDirective,
  ShEmptyDirective,
} from '../table/table/table.directives';
import { NoteFormComponent } from './note-form.component';

/** Pinned notes first; the order within each group is kept. */
function sortNotes(notes: NoteModel[]): NoteModel[] {
  return [...notes].sort((a, b) =>
    !!a.pinned === !!b.pinned ? 0 : a.pinned ? -1 : 1
  );
}

/**
 * The notes of an object (add, edit, delete, pin) as a `surface-card` with an
 * `sh-table`: the `@seahorse/shell` counterpart of `temp-notes-card`. The list
 * is replaced, never mutated, because `sh-table` refreshes on a new reference.
 *
 *   <sh-notes-card [objectId]="user.id" objectType="$users_user_id" />
 */
@Component({
  selector: 'sh-notes-card',
  templateUrl: './notes-card.component.html',
  host: { class: 'd-block' },
  imports: [
    CommonModule,
    TranslateModule,
    ShButtonComponent,
    ShTableComponent,
    ShCellDirective,
    ShEmptyDirective,
  ],
})
export class NotesCardComponent implements OnInit, OnDestroy {
  @Input() allowAdd = true;
  @Input() allowDelete = true;
  @Input() allowEdit = true;
  @Input() objectId: string;
  @Input() objectType: string;

  loading = false;
  notes: NoteModel[] = [];

  private readonly _subscriptions = new Subscription();

  private readonly _noteService = inject(NoteService);
  private readonly _dialogService = inject(DialogService);

  // Stable fields, not getters: they feed sh-table inputs.
  readonly columns: ShColumn<NoteModel>[] = [
    { key: 'description', header: 'shared.terms.description', type: 'custom' },
    { key: 'remarks', header: 'shared.terms.remarks', type: 'text' },
  ];

  readonly rowActions: ActionMenuItem<NoteModel>[] = [
    {
      label: (note) => (note.pinned ? 'notes.unpin' : 'notes.pin'),
      icon: 'fa-solid fa-thumbtack',
      command: (note) => this.changePinStatus(note),
    },
    {
      label: 'shared.terms.edit',
      icon: 'fa-solid fa-pencil-alt',
      command: (note) => this.edit(note),
    },
    {
      label: 'shared.terms.delete',
      icon: 'fa-solid fa-trash',
      danger: true,
      visible: () => this.allowDelete === true,
      command: (note) => this.delete(note),
    },
  ];

  ngOnInit(): void {
    if (this.objectId && this.objectType) this.get();
  }

  get() {
    this.loading = true;

    this._subscriptions.add(
      this._noteService.get(this.objectId, this.objectType).subscribe((res) => {
        this.notes = sortNotes(res.result ?? []);
        this.loading = false;
      })
    );
  }

  add() {
    this._subscriptions.add(
      this._dialogService
        .open(NoteFormComponent, { title: 'notes.add', data: {} })
        .afterOk.subscribe((newNote) => {
          newNote.objectId = this.objectId;
          newNote.objectType = this.objectType;
          this._subscriptions.add(
            this._noteService.add(newNote).subscribe((res) => {
              if (res.status === ResultStatus.OK) {
                this.notes = sortNotes([...this.notes, res.result]);
              }
            })
          );
        })
    );
  }

  onRowOpen(note: NoteModel) {
    if (this.allowEdit) this.edit(note);
  }

  edit(note: NoteModel) {
    this._subscriptions.add(
      this._dialogService
        .open(NoteFormComponent, { title: 'notes.edit', data: { note } })
        .afterOk.subscribe((updatedNote) => {
          this._subscriptions.add(
            this._noteService.update(updatedNote).subscribe((res) => {
              if (res.status === ResultStatus.OK) {
                this.notes = sortNotes(
                  this.notes.map((n) =>
                    n.id === updatedNote.id ? updatedNote : n
                  )
                );
              }
            })
          );
        })
    );
  }

  delete(note: NoteModel) {
    this._subscriptions.add(
      this._dialogService
        .confirm({
          title: 'shared.terms.delete',
          message: 'shared.terms.areYouSure',
          confirmLabel: 'shared.terms.delete',
          cancelLabel: 'shared.terms.cancel',
          severity: 'danger',
        })
        .subscribe((confirmed) => {
          if (!confirmed) return;

          this._subscriptions.add(
            this._noteService.delete(note.id).subscribe((res) => {
              if (res.status === ResultStatus.Deleted) {
                this.notes = this.notes.filter((n) => n.id !== note.id);
              }
            })
          );
        })
    );
  }

  changePinStatus(note: NoteModel) {
    const updatedNote: NoteModel = { ...note, pinned: !note.pinned };
    this.notes = sortNotes(
      this.notes.map((n) => (n.id === note.id ? updatedNote : n))
    );

    this._subscriptions.add(this._noteService.update(updatedNote).subscribe());
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
